import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Menu,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Info, Edit, Close, Link as LinkIcon, MoreVert } from '@mui/icons-material';
import { toast } from 'react-toastify';

const ViewDataGrid = () => {
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [allTables, setAllTables] = useState([]); // Store all available tables for dropdown options
  const [tableData, setTableData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [application, setApplication] = useState('');
  const [category, setCategory] = useState('');
  const [selectedTableName, setSelectedTableName] = useState('');

  // Pagination
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [totalCount, setTotalCount] = useState(0);

  // Metadata/Context
  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);
  const [metadataLoading, setMetadataLoading] = useState(false);

  // Options Menu
  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
  const optionsMenuOpen = Boolean(optionsAnchorEl);
  const [metadata, setMetadata] = useState({
    hftmId: null,
    tenantId: null,
    category: '',
    subCategory: '',
    tableName: '',
    sqlStatement: '',
    description: '',
    agentId: null,
    context: '',
  });

  // Get unique applications and categories for filters from ALL tables (not filtered by selection)
  const applications = [...new Set(allTables.map(t => t.application).filter(Boolean))].sort();
  const categories = [...new Set(allTables.map(t => t.category).filter(Boolean))].sort();

  useEffect(() => {
    fetchTableOptions();
  }, []);

  useEffect(() => {
    if (application !== '' && category !== '') {
      fetchTables();
    } else {
      setTables([]);
      setSelectedTableName('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application, category]);

  useEffect(() => {
    if (selectedTableName) {
      fetchTableData(selectedTableName, paginationModel.page, paginationModel.pageSize);
      // Fetch metadata when table is selected
      fetchMetadata(selectedTableName);
    } else {
      setTableData([]);
      setColumns([]);
      setTotalCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTableName, paginationModel.page, paginationModel.pageSize]);

  const fetchTableOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}synctrack/hubflow-datapoint/tables`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAllTables(data.tables || []);
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to fetch filter options');
      }
    } catch (err) {
      toast.error('Error fetching filter options');
    }
  };

  const fetchTables = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (application && application !== 'ALL') params.append('application', application);
      if (category && category !== 'ALL') params.append('category', category);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}synctrack/hubflow-datapoint/tables?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const fetchedTables = data.tables || [];
        setTables(fetchedTables);

        if (selectedTableName && !fetchedTables.find(t => t.tableName === selectedTableName)) {
          setSelectedTableName('');
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch tables');
        toast.error('Failed to fetch tables');
      }
    } catch (err) {
      setError(err.message);
      toast.error('Error fetching tables');
    } finally {
      setLoading(false);
    }
  };

  const fetchTableData = async (tableName, pageNum = 0, pageSize = 50) => {
    setDataLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}synctrack/hubflow-datapoint/tables/${encodeURIComponent(tableName)}/data?page=${pageNum}&size=${pageSize}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Add id field to each row for DataGrid
        const rowsWithId = (data.data || []).map((row, idx) => ({
          id: idx + (pageNum * pageSize),
          ...row,
        }));
        setTableData(rowsWithId);
        setColumns(data.columns || []);
        setTotalCount(data.totalCount || 0);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch table data');
        toast.error('Failed to fetch table data');
      }
    } catch (err) {
      setError(err.message);
      toast.error('Error fetching table data');
    } finally {
      setDataLoading(false);
    }
  };

  const handleTableSelect = (tableName) => {
    setSelectedTableName(tableName);
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
  };

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const fetchMetadata = async (tableName) => {
    if (!tableName) return;

    setMetadataLoading(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenantId') || '1';

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}synctrack/hubflow-datapoint/tables/${encodeURIComponent(tableName)}/metadata?tenantId=${tenantId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.exists && data.metadata) {
          setMetadata(data.metadata);
        } else {
          // Initialize with table name and category/sub-category from selected table
          const selectedTable = tables.find(t => t.tableName === tableName);
          setMetadata({
            hftmId: null,
            tenantId: parseInt(tenantId),
            category: selectedTable?.category || category || '',
            subCategory: selectedTable?.subCategory || '',
            tableName: tableName,
            sqlStatement: '',
            description: '',
            agentId: null,
            context: '',
          });
        }
      } else {
        // Initialize even on error
        const selectedTable = tables.find(t => t.tableName === tableName);
        setMetadata({
          hftmId: null,
          tenantId: parseInt(tenantId),
          category: selectedTable?.category || category || '',
          subCategory: selectedTable?.subCategory || '',
          tableName: tableName,
          sqlStatement: '',
          description: '',
          agentId: null,
          context: '',
        });
      }
    } catch (err) {
      console.error('Error fetching metadata:', err);
    } finally {
      setMetadataLoading(false);
    }
  };

  const handleOpenMetadataDialog = () => {
    setMetadataDialogOpen(true);
  };

  const handleCloseMetadataDialog = () => {
    setMetadataDialogOpen(false);
  };

  const handleSaveMetadata = async () => {
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenantId') || '1';

      // Ensure tenantId is set
      const metadataToSave = {
        ...metadata,
        tenantId: metadata.tenantId || parseInt(tenantId),
        tableName: selectedTableName,
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}synctrack/hubflow-datapoint/tables/metadata`,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(metadataToSave),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMetadata(data.metadata);
        toast.success('Context saved successfully');
        handleCloseMetadataDialog();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to save context');
      }
    } catch (err) {
      toast.error('Error saving context');
      console.error('Error saving metadata:', err);
    }
  };

  // Convert columns to DataGrid format
  const dataGridColumns = useMemo(() => {
    return columns.map((col) => ({
      field: col,
      headerName: col,
      width: 200,
      flex: 1,
      minWidth: 150,
      headerClassName: 'grid-header',
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined) {
          return <em style={{ color: '#999' }}>null</em>;
        }
        if (typeof value === 'object') {
          return JSON.stringify(value);
        }
        return String(value);
      },
    }));
  }, [columns]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Application</InputLabel>
                <Select
                  value={application}
                  label="Application"
                  onChange={(e) => {
                    setApplication(e.target.value);
                    setCategory('');
                    setSelectedTableName('');
                  }}
                >
                  <MenuItem value="ALL">ALL</MenuItem>
                  {applications.map((app) => (
                    <MenuItem key={app} value={app}>
                      {app}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={category}
                  label="Category"
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setSelectedTableName('');
                  }}
                >
                  <MenuItem value="ALL">ALL</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Table</InputLabel>
                <Select
                  value={selectedTableName}
                  label="Table"
                  onChange={(e) => handleTableSelect(e.target.value)}
                  disabled={loading || application === '' || category === '' || tables.length === 0}
                >
                  <MenuItem value="">
                    <em>Select a table</em>
                  </MenuItem>
                  {tables.map((table) => (
                    <MenuItem key={table.tableName} value={table.tableName}>
                      {table.tableName}
                      {table.application && (
                        <span style={{ color: '#999', marginLeft: '8px', fontSize: '0.875rem' }}>
                          ({table.application}
                          {table.category && ` - ${table.category}`})
                        </span>
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ visibility: 'hidden' }}>Options</InputLabel>
                <IconButton
                  onClick={(e) => setOptionsAnchorEl(e.currentTarget)}
                  disabled={loading}
                  sx={{
                    height: '40px',
                    width: '40px',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: '4px',
                    '&:hover': {
                      borderColor: 'rgba(0, 0, 0, 0.87)',
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    }
                  }}
                >
                  <MoreVert />
                </IconButton>
                <Menu
                  anchorEl={optionsAnchorEl}
                  open={optionsMenuOpen}
                  onClose={() => setOptionsAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      setOptionsAnchorEl(null);
                      navigate('/tenant-link-relationships');
                    }}
                  >
                    <LinkIcon sx={{ mr: 1 }} fontSize="small" />
                    Link Relationships
                  </MenuItem>
                </Menu>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Data Grid */}
      <Card>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {selectedTableName ? (
            <>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6">
                    {selectedTableName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {totalCount} {totalCount === 1 ? 'record' : 'records'}
                  </Typography>
                </Box>
                <Tooltip title="View/Edit Context">
                  <IconButton
                    onClick={handleOpenMetadataDialog}
                    color="primary"
                    size="small"
                  >
                    <Info />
                  </IconButton>
                </Tooltip>
              </Box>
              {dataLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ height: 'calc(100vh - 400px)', minHeight: 400, width: '100%' }}>
                  <DataGrid
                    rows={tableData}
                    columns={dataGridColumns}
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelChange}
                    pageSizeOptions={[25, 50, 100]}
                    rowCount={totalCount}
                    paginationMode="server"
                    loading={dataLoading}
                    disableSelectionOnClick
                    autoHeight={false}
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      '& .grid-header': {
                        backgroundColor: '#f5f5f5',
                        fontWeight: 600,
                      },
                      '& .MuiDataGrid-cell': {
                        borderBottom: '1px solid #e0e0e0',
                      },
                      '& .MuiDataGrid-row:hover': {
                        backgroundColor: '#f9f9f9',
                      },
                    }}
                    components={{
                      NoRowsOverlay: () => (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            flexDirection: 'column',
                            gap: 2,
                          }}
                        >
                          <Typography color="text.secondary">
                            No data available
                          </Typography>
                        </Box>
                      ),
                    }}
                  />
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Select a table from the dropdown to view its data
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Metadata/Context Dialog */}
      <Dialog
        open={metadataDialogOpen}
        onClose={handleCloseMetadataDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Table Context - {selectedTableName}
            </Typography>
            <IconButton onClick={handleCloseMetadataDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {metadataLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Category"
                  value={metadata.category}
                  onChange={(e) => setMetadata({ ...metadata, category: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Sub Category"
                  value={metadata.subCategory}
                  onChange={(e) => setMetadata({ ...metadata, subCategory: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={metadata.description}
                  onChange={(e) => setMetadata({ ...metadata, description: e.target.value })}
                  multiline
                  rows={2}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="SQL Statement"
                  value={metadata.sqlStatement}
                  onChange={(e) => setMetadata({ ...metadata, sqlStatement: e.target.value })}
                  multiline
                  rows={4}
                  size="small"
                  placeholder="Optional SQL query or statement related to this table"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Context"
                  value={metadata.context}
                  onChange={(e) => setMetadata({ ...metadata, context: e.target.value })}
                  multiline
                  rows={6}
                  size="small"
                  placeholder="Add detailed context, notes, business rules, or documentation about this table..."
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMetadataDialog}>Cancel</Button>
          <Button onClick={handleSaveMetadata} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ViewDataGrid;

